/**
 * agent-channel-registry.ts
 *
 * Persistent Registry for Agent Channels & RBAC Configuration.
 */

import {
  AgentChannelDefinition,
  StandardAgentChannelId,
} from './agent-channel-contracts.js';
import { DEFAULT_GROUNDING_POLICIES } from '../grounding/rag-collection-registry.js';
import { ChannelAccessController, UserAccessContext } from '../access/channel-access-controller.js';
import type { OrchestrationStore } from '../../mesh/orchestration/storage/orchestration-store.js';

export class AgentChannelRegistry {
  private channels = new Map<string, AgentChannelDefinition>();
  private accessController = new ChannelAccessController();

  constructor(private readonly store?: OrchestrationStore) {
    this.seedDefaultChannels();
  }

  private seedDefaultChannels(): void {
    const defaultChannels: AgentChannelDefinition[] = [
      {
        id: 'shopping',
        name: 'Commerce Shopping Assistant',
        description: 'Natural language shopping discovery, product comparison, policy grounding, and cart management',
        icon: '🛍️',
        modelKind: 'llm',
        llmModelId: 'gemma-2-2b-it',
        needleModelId: 'needle-2',
        pluginId: 'commerce',
        ragCollectionIds: ['plugin:commerce:catalog', 'plugin:commerce:policies'],
        groundingPolicy: DEFAULT_GROUNDING_POLICIES.shopping!,
        tools: ['search_products', 'compare_products', 'check_stock', 'cart.add_item', 'cart.checkout'],
        actionPermissions: [
          { toolName: 'search_products', allowedRoles: ['guest', 'member', 'sales', 'support', 'developer', 'admin', 'owner'], allowedGroupIds: [], allowedPrincipalIds: [], deniedPrincipalIds: [] },
          { toolName: 'compare_products', allowedRoles: ['guest', 'member', 'sales', 'support', 'developer', 'admin', 'owner'], allowedGroupIds: [], allowedPrincipalIds: [], deniedPrincipalIds: [] },
          { toolName: 'cart.add_item', allowedRoles: ['member', 'sales', 'support', 'developer', 'admin', 'owner'], allowedGroupIds: [], allowedPrincipalIds: [], deniedPrincipalIds: [] },
          { toolName: 'cart.checkout', allowedRoles: ['member', 'sales', 'support', 'developer', 'admin', 'owner'], allowedGroupIds: [], allowedPrincipalIds: [], deniedPrincipalIds: [] },
        ],
        accessPolicy: {
          publicAccess: 'authenticated',
          allowedRoles: ['member', 'developer', 'sales', 'support', 'admin', 'owner'],
          allowedUserGroupIds: [],
          allowedPrincipalIds: [],
          deniedPrincipalIds: [],
        },
        enabled: true,
        isScaffolded: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: 'wiki',
        name: 'Sovereign Knowledge Wiki Assistant',
        description: 'Verifiable Q&A with strict citation bounds against architectural manuals and platform RFCs',
        icon: '📚',
        modelKind: 'llm',
        llmModelId: 'gemma-2-2b-it',
        needleModelId: 'needle-2',
        pluginId: 'llmwiki',
        ragCollectionIds: ['global:documentation-all'],
        groundingPolicy: DEFAULT_GROUNDING_POLICIES.wiki!,
        tools: ['search_wiki', 'synthesize_documents', 'verify_claim'],
        accessPolicy: {
          publicAccess: 'authenticated',
          allowedRoles: ['member', 'developer', 'sales', 'support', 'admin', 'owner'],
          allowedUserGroupIds: [],
          allowedPrincipalIds: [],
          deniedPrincipalIds: [],
        },
        enabled: true,
        isScaffolded: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: 'scrum',
        name: 'Scrum Copilot & Backlog Planner',
        description: 'Discussion-to-backlog parsing, structured acceptance criteria generation, and sprint planning',
        icon: '🎯',
        modelKind: 'llm',
        llmModelId: 'gemma-2-2b-it',
        needleModelId: 'needle-2',
        pluginId: 'scrum-master',
        ragCollectionIds: ['plugin:scrum-master:conventions', 'plugin:scrum-master:arch-rfcs'],
        groundingPolicy: DEFAULT_GROUNDING_POLICIES.scrum!,
        tools: ['extract_stories', 'generate_acceptance_criteria', 'identify_risks', 'scrum.create_story', 'scrum.create_sprint'],
        actionPermissions: [
          { toolName: 'extract_stories', allowedRoles: ['developer', 'admin', 'owner'], allowedGroupIds: ['eng-team'], allowedPrincipalIds: [], deniedPrincipalIds: [] },
          { toolName: 'scrum.create_story', allowedRoles: ['developer', 'admin', 'owner'], allowedGroupIds: ['eng-team'], allowedPrincipalIds: [], deniedPrincipalIds: [] },
          { toolName: 'scrum.create_sprint', allowedRoles: ['admin', 'owner'], allowedGroupIds: ['scrum-masters'], allowedPrincipalIds: [], deniedPrincipalIds: [] },
        ],
        accessPolicy: {
          publicAccess: 'disabled',
          allowedRoles: ['developer', 'admin', 'owner'],
          allowedUserGroupIds: ['eng-team', 'scrum-masters'],
          allowedPrincipalIds: [],
          deniedPrincipalIds: [],
        },
        enabled: true,
        isScaffolded: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: 'crm',
        name: 'CRM Conversational Assistant',
        description: 'Customer interaction logging, consent ledger checking, and deal pipeline management',
        icon: '👥',
        modelKind: 'llm',
        llmModelId: 'gemma-2-2b-it',
        needleModelId: 'needle-2',
        pluginId: 'crm',
        ragCollectionIds: ['plugin:crm:consent', 'plugin:crm:customer-policies'],
        groundingPolicy: DEFAULT_GROUNDING_POLICIES.crm!,
        tools: ['crm.create_lead', 'crm.log_interaction', 'crm.update_deal'],
        accessPolicy: {
          publicAccess: 'disabled',
          allowedRoles: ['sales', 'support', 'admin', 'owner'],
          allowedUserGroupIds: ['sales-team', 'support-team'],
          allowedPrincipalIds: [],
          deniedPrincipalIds: [],
        },
        enabled: true,
        isScaffolded: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: 'cms',
        name: 'CMS Content & Edge Publishing Assistant',
        description: 'Article drafting, structured block extraction, taxonomy tagging, and CDN deployment',
        icon: '✍️',
        modelKind: 'llm',
        llmModelId: 'gemma-2-2b-it',
        needleModelId: 'needle-2',
        pluginId: 'cms',
        ragCollectionIds: ['plugin:cms:styleguide', 'plugin:cms:taxonomies'],
        groundingPolicy: DEFAULT_GROUNDING_POLICIES.cms!,
        tools: ['cms.create_page', 'cms.publish_page'],
        accessPolicy: {
          publicAccess: 'disabled',
          allowedRoles: ['admin', 'owner'],
          allowedUserGroupIds: ['content-editors'],
          allowedPrincipalIds: [],
          deniedPrincipalIds: [],
        },
        enabled: true,
        isScaffolded: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: 'advisor',
        name: 'Strategic Planning Advisor',
        description: 'Cross-domain multi-step planning, dependency mapping, and resource allocation',
        icon: '🧭',
        modelKind: 'llm',
        llmModelId: 'gemma-2-2b-it',
        needleModelId: 'needle-2',
        pluginId: 'action-planner',
        ragCollectionIds: ['channel:advisor:cross-domain'],
        groundingPolicy: DEFAULT_GROUNDING_POLICIES.advisor!,
        tools: ['advisor.create_plan', 'advisor.map_dependencies'],
        accessPolicy: {
          publicAccess: 'disabled',
          allowedRoles: ['admin', 'owner'],
          allowedUserGroupIds: ['executive-team'],
          allowedPrincipalIds: [],
          deniedPrincipalIds: [],
        },
        enabled: true,
        isScaffolded: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ];

    for (const ch of defaultChannels) {
      this.channels.set(ch.id, ch);
    }
  }

  getChannel(channelId: StandardAgentChannelId): AgentChannelDefinition | undefined {
    return this.channels.get(channelId);
  }

  listAllChannels(): readonly AgentChannelDefinition[] {
    return Array.from(this.channels.values());
  }

  /**
   * Filters channels accessible to the specific authenticated user.
   */
  listAuthorizedChannels(user: UserAccessContext): readonly AgentChannelDefinition[] {
    return Array.from(this.channels.values()).filter((ch) => {
      const verdict = this.accessController.evaluateChannelAccess(ch, user);
      return verdict.allowed;
    });
  }

  registerChannel(channel: AgentChannelDefinition): void {
    this.channels.set(channel.id, channel);
  }

  setChannelStatus(channelId: string, enabled: boolean): AgentChannelDefinition {
    const existing = this.channels.get(channelId);
    if (!existing) throw new Error(`Channel '${channelId}' not found`);

    const updated: AgentChannelDefinition = {
      ...existing,
      enabled,
      updatedAt: new Date().toISOString(),
    };
    this.channels.set(channelId, updated);
    return updated;
  }
}
